import { appVersion, compareISODate, instantISO, todayISO, type ISODate } from '@mononoke/shared';
import { localStore } from './browser.js';
import { parseRoute, subscribeRoute } from './router.js';
import { scrollLockDepth } from './scroll-lock.js';

/**
 * THE iOS LAYOUT PROBE (0.1.520) — the instrument, after the theories ran out.
 *
 * The iPhone layout has been "fixed" at 0.1.248, 0.1.360 and 0.1.395, every time reasoned from CSS
 * and a screenshot, because nobody who builds this app owns an iPhone: this machine has no WebKit,
 * the maintainer has no iOS device, and the one person who sees the bug (Farah, in Antwerp) should
 * not be asked to collect anything. CLAUDE.md's debugging rule — after one failed fix, stop
 * theorising and instrument — had never been applied to iOS because there was no way to. This is
 * the way.
 *
 * WHAT IT DOES. On an iPhone it measures the layout at every new state (a screen, a rotation, the
 * keyboard, a sheet) and sends ONE Sentry event per distinct state — through the tunnel monitoring
 * already uses, so no new processor, table or secret (maintainer ruling, 1 Oct 2026). The user does
 * nothing and sees nothing.
 *
 * WHAT LEAVES THE PHONE: numbers and class names. The viewport in every unit iOS disagrees about
 * (`innerHeight`, `visualViewport`, 100vh / dvh / svh / lvh), the real `env(safe-area-inset-*)`,
 * and a WIREFRAME — every visible element's box and class path — which `tools/layout-wireframe.mjs`
 * redraws as a picture. NEVER text, never an image, never a record id (route NAME only, uuids and
 * long digit runs scrubbed from class/id strings), never the raw user agent (parsed to an iOS
 * version and a browser family, in line with monitoring.ts's `userInfo: false`).
 *
 * BOUNDED THREE WAYS, so it cannot eat the Sentry quota real errors need:
 *   1. iPhones only (ruling: "all iPhones" — iPads are one regex term away, deliberately not in);
 *   2. a state is sent once per app version (the fingerprint below), so a fix's deploy re-sends
 *      the same screens and the "after" arrives with nobody asking;
 *   3. at most DAILY_CAP events a day per device, and NOTHING after LAYOUT_PROBE_UNTIL.
 *
 * ⚠️ THE OFF DATE IS BAKED IN (ruling: "auto-off in 21 days"). Extending it is a release; letting
 * it lapse needs no one. When the bug is fixed, delete this file and its two call sites rather
 * than leaving a dead probe behind.
 */

/**
 * Last calendar day (Europe/Brussels) the probe sends. Was 22 Oct (21 days from 1 Oct); extended
 * by the maintainer on 19 Oct 2026 to 21 days from that day, because until 0.1.535 our own Sentry
 * tunnel refused every probe event (415) and the first window delivered nothing.
 */
export const LAYOUT_PROBE_UNTIL: ISODate = '2026-11-12';
export const DAILY_CAP = 30;
/** Quiet time after the last trigger before measuring — lets a sheet finish animating in. */
const SETTLE_MS = 1200;
const MAX_NODES = 900;
const MAX_FLAGS = 24;
const STORE_KEY = 'mononoke.layout-probe.v1';
/**
 * `?layoutprobe=1` forces the probe on ANY device for this tab (kept in sessionStorage so it
 * survives the app's own navigation). It is how the probe is exercised off-iPhone — the e2e
 * harness, a desk browser in device mode — and with monitoring off it prints instead of sending.
 */
const FORCE_PARAM = 'layoutprobe';
const FORCE_KEY = 'mononoke.layout-probe.force';

// --- pure parts (unit-tested) -------------------------------------------------------------------

export function isIPhone(ua: string): boolean {
  return /\b(iPhone|iPod)\b/.test(ua);
}

/** "17.4" from `… CPU iPhone OS 17_4 like Mac OS X …`; null when the agent does not say. */
export function iosVersion(ua: string): string | null {
  const m = /\bOS (\d+)_(\d+)(?:_(\d+))? like Mac OS X/.exec(ua);
  if (!m) return null;
  return m[3] ? `${m[1]}.${m[2]}.${m[3]}` : `${m[1]}.${m[2]}`;
}

/** Every iOS browser is WebKit; the family still matters because their chrome differs. */
export function iosBrowser(ua: string): 'safari' | 'chrome' | 'firefox' | 'edge' | 'other' {
  if (/CriOS\//.test(ua)) return 'chrome';
  if (/FxiOS\//.test(ua)) return 'firefox';
  if (/EdgiOS\//.test(ua)) return 'edge';
  if (/Version\/[\d.]+.*Safari\//.test(ua)) return 'safari';
  // An installed home-screen app reports no `Safari/` token at all — `standalone` says which.
  return 'other';
}

export function probeShouldRun(opts: { ua: string; today: ISODate; forced: boolean }): boolean {
  if (compareISODate(opts.today, LAYOUT_PROBE_UNTIL) > 0) return false;
  return opts.forced || isIPhone(opts.ua);
}

/** Class/id text with anything record-shaped removed: uuids and runs of 4+ digits. */
export function scrub(text: string): string {
  return text
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '~')
    .replace(/\d{4,}/g, '~');
}

/** [x, y, w, h], rounded to whole CSS pixels. */
export type Rect = [number, number, number, number];

export interface WireNode {
  /** `tag#id.class1.class2.class3`, scrubbed, at most 80 chars. */
  s: string;
  /** Index of the nearest INCLUDED ancestor in `nodes`, -1 for a top-level one. */
  u: number;
  r: Rect;
  /** Own `position` when it is not static/relative. */
  p?: 'fixed' | 'sticky' | 'absolute';
  /** 1 when this node or an ancestor is fixed/sticky — it stays put while the page scrolls. */
  f?: 1;
  /** 1 when this node or an ancestor clips horizontally (overflow-x is not visible). */
  c?: 1;
  /** 1 for a control a finger aims at: a, button, input, select, textarea, [role=button]. */
  i?: 1;
  z?: number;
}

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface VisualViewportBox {
  w: number;
  h: number;
  top: number;
  left: number;
  scale: number;
}

export interface Flag {
  code:
    | 'page-wider-than-screen'
    | 'page-scrolled-sideways'
    | 'element-past-edge'
    | 'control-under-status-bar'
    | 'control-under-home-bar'
    | 'pinned-below-visible-area'
    | 'taller-than-visible-area'
    | 'viewport-shifted'
    | 'zoomed';
  /** The offending element's selector, when there is one. */
  s?: string;
  /** By how many CSS pixels. */
  by?: number;
}

export interface FlagInput {
  vw: number;
  vh: number;
  visual: VisualViewportBox | null;
  insets: Insets;
  docScrollWidth: number;
  clientWidth: number;
  scrollX: number;
  /** A text field has focus — the keyboard is (probably) up. */
  keyboard: boolean;
  nodes: WireNode[];
}

/**
 * The failure classes the earlier iOS fixes were each guessing at, decided ON THE PHONE so the
 * Sentry title already says which one fired. Every rule compares two numbers the snapshot also
 * carries, so a flag can be re-checked by hand from the same event.
 */
export function computeFlags(input: FlagInput): Flag[] {
  const flags: Flag[] = [];
  const { vw, vh, insets, nodes } = input;
  const visibleTop = input.visual?.top ?? 0;
  const visibleBottom = input.visual ? input.visual.top + input.visual.h : vh;

  if (input.docScrollWidth > input.clientWidth + 1) {
    flags.push({ code: 'page-wider-than-screen', by: input.docScrollWidth - input.clientWidth });
  }
  if (input.scrollX !== 0) flags.push({ code: 'page-scrolled-sideways', by: input.scrollX });
  if (input.visual && input.visual.scale > 1.01) {
    flags.push({ code: 'zoomed', by: Math.round(input.visual.scale * 100) / 100 });
  }
  // At scale 1 only: a pinch-zoom shrinks the visual viewport by design, and is flagged above.
  if (input.visual && !input.keyboard && input.visual.scale <= 1.01 && (input.visual.top > 1 || input.visual.h < vh - 1)) {
    flags.push({ code: 'viewport-shifted', by: Math.round(Math.max(input.visual.top, vh - input.visual.h)) });
  }

  // Past the edge: the OUTERMOST unclipped offender only — its children all offend with it, and
  // twenty copies of one cause read as twenty causes.
  const offending = new Set<number>();
  nodes.forEach((n, index) => {
    const [x, , w] = n.r;
    if (w <= 0 || n.c === 1) return;
    const over = Math.max(x + w - vw, -x);
    if (over <= 1) return;
    offending.add(index);
    if (n.u >= 0 && offending.has(n.u)) return;
    flags.push({ code: 'element-past-edge', s: n.s, by: Math.round(over) });
  });

  for (const n of nodes) {
    const [, y, , h] = n.r;
    if (h <= 0) continue;
    // Only what stays put: content scrolling under a translucent status bar is normal iOS.
    if (n.f === 1 && n.i === 1) {
      if (insets.top > 0 && y < insets.top - 1 && y + h > 0) {
        flags.push({ code: 'control-under-status-bar', s: n.s, by: Math.round(insets.top - y) });
      }
      if (insets.bottom > 0 && y + h > vh - insets.bottom + 1 && y < vh) {
        flags.push({ code: 'control-under-home-bar', s: n.s, by: Math.round(y + h - (vh - insets.bottom)) });
      }
    }
    if (n.p === 'fixed') {
      if (y >= visibleTop && y < visibleBottom && y + h > visibleBottom + 1) {
        flags.push({ code: 'pinned-below-visible-area', s: n.s, by: Math.round(y + h - visibleBottom) });
      }
      if (h > visibleBottom - visibleTop + 1) {
        flags.push({ code: 'taller-than-visible-area', s: n.s, by: Math.round(h - (visibleBottom - visibleTop)) });
      }
    }
  }
  return flags.slice(0, MAX_FLAGS);
}

/** The cheap identity of a layout state — computed BEFORE the expensive walk, so an already-sent
 * state costs one string. Viewport heights round to 10px: the toolbar's slide is jitter, the
 * keyboard (~300px) is not. */
export function fingerprint(parts: {
  version: string;
  route: string;
  orientation: string;
  vw: number;
  vh: number;
  visualH: number | null;
  overlays: string[];
  focus: string | null;
}): string {
  const r10 = (n: number): number => Math.round(n / 10) * 10;
  return [
    parts.version,
    parts.route,
    parts.orientation,
    `${String(parts.vw)}x${String(r10(parts.vh))}`,
    parts.visualH === null ? '-' : String(r10(parts.visualH)),
    parts.overlays.join(','),
    parts.focus ?? '-',
  ].join('|');
}

export interface ProbeStore {
  day: ISODate;
  count: number;
  sent: string[];
}

/** Decide whether a state may be sent, and the store as it will be after sending it. */
export function admit(store: ProbeStore | null, today: ISODate, print: string): { ok: boolean; next: ProbeStore } {
  const base: ProbeStore =
    store && store.day === today ? store : { day: today, count: 0, sent: store?.sent ?? [] };
  if (base.sent.includes(print) || base.count >= DAILY_CAP) return { ok: false, next: base };
  return { ok: true, next: { day: today, count: base.count + 1, sent: [...base.sent, print].slice(-200) } };
}

// --- the measurement (needs a real layout engine) -----------------------------------------------

export interface LayoutSnapshot {
  v: 1;
  version: string;
  route: string;
  at: string;
  device: {
    ios: string | null;
    browser: ReturnType<typeof iosBrowser>;
    standalone: boolean;
    displayMode: string;
    dpr: number;
    screen: [number, number];
    orientation: string;
  };
  viewport: {
    inner: [number, number];
    client: [number, number];
    visual: VisualViewportBox | null;
    scroll: [number, number];
    doc: [number, number];
  };
  units: { vh: number; dvh: number; svh: number; lvh: number; fillAvailable: number };
  insets: Insets;
  env: {
    rootFontPx: number;
    textSizeAdjust: string;
    theme: string;
    scrollLocked: boolean;
    focus: string | null;
    overlays: string[];
  };
  flags: Flag[];
  nodes: WireNode[];
  /** Elements measured but dropped for MAX_NODES — never silently: the count says so. */
  truncated: number;
}

const OVERLAY_SELECTOR = '[role="dialog"], [aria-modal="true"], .app-nav-sheet-open';
const INTERACTIVE = new Set(['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'SUMMARY', 'LABEL']);
/** Painted by their parent; the parent's box is the useful one. */
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'BR', 'PATH', 'G', 'DEFS', 'USE', 'CIRCLE', 'RECT', 'LINE', 'POLYLINE', 'POLYGON', 'ELLIPSE', 'TITLE', 'STOP', 'LINEARGRADIENT', 'CLIPPATH', 'MASK']);

function focusKind(): string | null {
  const el = document.activeElement;
  if (!el || el === document.body) return null;
  if (el instanceof HTMLInputElement) return `input:${el.type}`;
  if (el instanceof HTMLTextAreaElement) return 'textarea';
  if (el instanceof HTMLSelectElement) return 'select';
  if (el instanceof HTMLElement && el.isContentEditable) return 'editable';
  return null;
}

function overlays(): string[] {
  const names = new Set<string>();
  for (const el of Array.from(document.querySelectorAll(OVERLAY_SELECTOR))) {
    names.add(scrub(el.classList[0] ?? el.tagName.toLowerCase()));
  }
  return [...names].sort();
}

function orientation(): string {
  return window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait';
}

function routeName(): string {
  return parseRoute(window.location.pathname + window.location.search).name;
}

function selectorOf(el: Element): string {
  let s = el.tagName.toLowerCase();
  if (el.id) s += `#${el.id}`;
  for (const c of Array.from(el.classList).slice(0, 3)) s += `.${c}`;
  return scrub(s).slice(0, 80);
}

/** What the CSS engine resolves a length to, measured with a throwaway fixed element. */
function measureUnits(): { units: LayoutSnapshot['units']; insets: Insets } {
  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText =
    'position:fixed;left:0;top:0;width:0;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
  document.body.appendChild(probe);
  const px = (v: string): number => Math.round(Number.parseFloat(v) || 0);
  const cs = getComputedStyle(probe);
  const insets = { top: px(cs.paddingTop), right: px(cs.paddingRight), bottom: px(cs.paddingBottom), left: px(cs.paddingLeft) };
  const heightOf = (value: string): number => {
    probe.style.padding = '0';
    probe.style.height = value;
    return Math.round(probe.getBoundingClientRect().height);
  };
  const units = {
    vh: heightOf('100vh'),
    dvh: heightOf('100dvh'),
    svh: heightOf('100svh'),
    lvh: heightOf('100lvh'),
    fillAvailable: heightOf('-webkit-fill-available'),
  };
  probe.remove();
  return { units, insets };
}

/** One depth-first walk; ancestors' fixed/clip state flows down so no node re-walks its parents. */
function walk(): { nodes: WireNode[]; truncated: number } {
  const nodes: WireNode[] = [];
  let truncated = 0;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const visit = (el: Element, parent: number, pinned: boolean, clipped: boolean): void => {
    if (SKIP_TAGS.has(el.tagName.toUpperCase())) return;
    const cs = getComputedStyle(el);
    if (cs.display === 'none') return;
    const rect = el.getBoundingClientRect();
    const pos = cs.position;
    const isPinned = pinned || pos === 'fixed' || pos === 'sticky';
    const isClipped = clipped || (cs.overflowX !== 'visible' && el !== document.body && el !== document.documentElement);
    let index = parent;
    const sized = rect.width > 0 && rect.height > 0;
    // Near the screen only: a windowed list's far rows are not the layout anyone sees.
    const near = rect.bottom > -vh && rect.top < vh * 2 && rect.right > -vw && rect.left < vw * 2;
    if (sized && near && cs.visibility !== 'hidden') {
      if (nodes.length >= MAX_NODES) {
        truncated += 1;
      } else {
        const node: WireNode = {
          s: selectorOf(el),
          u: parent,
          r: [Math.round(rect.left), Math.round(rect.top), Math.round(rect.width), Math.round(rect.height)],
        };
        if (pos === 'fixed' || pos === 'sticky' || pos === 'absolute') node.p = pos;
        if (isPinned) node.f = 1;
        if (clipped) node.c = 1;
        if (INTERACTIVE.has(el.tagName) || el.getAttribute('role') === 'button') node.i = 1;
        const z = Number.parseInt(cs.zIndex, 10);
        if (Number.isFinite(z)) node.z = z;
        index = nodes.length;
        nodes.push(node);
      }
    }
    for (const child of Array.from(el.children)) visit(child, index, isPinned, isClipped);
  };
  for (const child of Array.from(document.body.children)) visit(child, -1, false, false);
  return { nodes, truncated };
}

export function collectSnapshot(): LayoutSnapshot {
  const ua = navigator.userAgent;
  const vv = window.visualViewport;
  const visual: VisualViewportBox | null = vv
    ? { w: Math.round(vv.width), h: Math.round(vv.height), top: Math.round(vv.offsetTop), left: Math.round(vv.offsetLeft), scale: vv.scale }
    : null;
  const root = document.documentElement;
  const { units, insets } = measureUnits();
  const { nodes, truncated } = walk();
  const focus = focusKind();
  const rootStyle = getComputedStyle(root);
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  const flags = computeFlags({
    vw: window.innerWidth,
    vh: window.innerHeight,
    visual,
    insets,
    docScrollWidth: root.scrollWidth,
    clientWidth: root.clientWidth,
    scrollX: Math.round(window.scrollX),
    keyboard: focus !== null && !/^input:(checkbox|radio|button|submit|range|color|file)$/.test(focus),
    nodes,
  });
  return {
    v: 1,
    version: appVersion(),
    route: routeName(),
    at: instantISO(),
    device: {
      ios: iosVersion(ua),
      browser: iosBrowser(ua),
      standalone,
      displayMode: ['standalone', 'fullscreen', 'minimal-ui', 'browser'].find((m) => window.matchMedia(`(display-mode: ${m})`).matches) ?? 'unknown',
      dpr: window.devicePixelRatio,
      screen: [window.screen.width, window.screen.height],
      orientation: orientation(),
    },
    viewport: {
      inner: [window.innerWidth, window.innerHeight],
      client: [root.clientWidth, root.clientHeight],
      visual,
      scroll: [Math.round(window.scrollX), Math.round(window.scrollY)],
      doc: [root.scrollWidth, root.scrollHeight],
    },
    units,
    insets,
    env: {
      rootFontPx: Number.parseFloat(rootStyle.fontSize) || 0,
      textSizeAdjust: rootStyle.getPropertyValue('-webkit-text-size-adjust') || rootStyle.getPropertyValue('text-size-adjust') || '',
      theme: root.dataset.theme ?? '',
      scrollLocked: scrollLockDepth() > 0,
      focus,
      overlays: overlays(),
    },
    flags,
    nodes,
    truncated,
  };
}

// --- the scheduler --------------------------------------------------------------------------------

function readStore(): ProbeStore | null {
  try {
    const raw = localStore?.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as ProbeStore) : null;
  } catch {
    return null; // an unreadable store re-sends at worst; the daily cap still holds this session
  }
}

function writeStore(store: ProbeStore): void {
  try {
    localStore?.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // Storage full or blocked: the in-memory copy below keeps this session deduplicated.
  }
}

function isForced(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get(FORCE_PARAM) === '1') {
      window.sessionStorage.setItem(FORCE_KEY, '1');
    }
    return window.sessionStorage.getItem(FORCE_KEY) === '1';
  } catch {
    return false;
  }
}

export interface ProbeSink {
  send(snapshot: LayoutSnapshot): void;
  /** A probe that throws is a defect in the probe — reported once a session, never to the user. */
  fail(error: unknown): void;
}

let started = false;

/**
 * Start measuring. Called once from main.tsx after the first render; a no-op off-iPhone, after
 * the off date, and on a second call.
 */
export function startLayoutProbe(sink: ProbeSink): void {
  if (started) return;
  if (!probeShouldRun({ ua: navigator.userAgent, today: todayISO(), forced: isForced() })) return;
  started = true;

  let store = readStore();
  let timer: number | undefined;
  let failed = false;

  const measure = (): void => {
    timer = undefined;
    try {
      const today = todayISO();
      if (compareISODate(today, LAYOUT_PROBE_UNTIL) > 0) return;
      const vv = window.visualViewport;
      const print = fingerprint({
        version: appVersion(),
        route: routeName(),
        orientation: orientation(),
        vw: window.innerWidth,
        vh: window.innerHeight,
        visualH: vv ? vv.height : null,
        overlays: overlays(),
        focus: focusKind(),
      });
      const verdict = admit(store, today, print);
      store = verdict.next;
      if (!verdict.ok) return;
      writeStore(store);
      sink.send(collectSnapshot());
    } catch (error) {
      if (!failed) {
        failed = true;
        sink.fail(error);
      }
    }
  };

  const schedule = (): void => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(measure, SETTLE_MS);
  };

  subscribeRoute(schedule);
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  window.visualViewport?.addEventListener('resize', schedule);
  document.addEventListener('focusin', schedule);
  document.addEventListener('focusout', schedule);
  // Sheets and the menu mount without a route change; a childList change on the root is the only
  // generic signal. Cheap: a schedule is a timer reset, and the fingerprint skips seen states.
  const root = document.getElementById('root');
  if (root) new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
  // Some sheets portal to <body> rather than mounting under #root.
  new MutationObserver(schedule).observe(document.body, { childList: true });
}
